import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Mail, Lock, Visibility, VisibilityOff } from '@mui/icons-material';
// import logo from '../../images/logo_veribase.png';
import logo from '../../images/logo_fiducia_full.png';
import AuthTabs from '../../components/Auth/AuthTabs';

import { useAuth } from '../../hooks/useAuth';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const { logIn, loading, error } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  // Debug log for redirect parameter
  useEffect(() => {
    const redirectPath = searchParams.get('redirect');
      }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const loggedInUser = await logIn({
      email: event.target.email.value,
      password: event.target.password.value
    });
    if (loggedInUser) {
      if (loggedInUser.roles?.includes('contract')) {
        navigate('/contract');
        return;
      }
      // Get the redirect path from URL parameters, default to home if not present
      const redirectPath = searchParams.get('redirect');
            navigate(redirectPath ? decodeURIComponent(redirectPath) : '/');
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Card
          elevation={0}
          sx={{
            width: '100%',
            background: theme === 'dark' 
              ? 'rgba(30, 30, 30, 0.73)'
              : 'rgba(255, 255, 255, 0.73)',
            backdropFilter: 'blur(10px)',
            border: '1px solid',
            borderColor: theme === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(255, 255, 255, 0.2)',
            borderRadius: 2,
            '& .MuiTypography-root': {
              fontFamily: '"Space Grotesk", sans-serif',
            },
            '& .MuiInputLabel-root': {
              fontFamily: '"Space Grotesk", sans-serif',
            },
            '& .MuiInputBase-input': {
              fontFamily: '"Space Grotesk", sans-serif',
            },
            '& .MuiButton-root': {
              fontFamily: '"Space Grotesk", sans-serif',
              fontWeight: 500,
            },
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <AuthTabs />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 4,
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Veritas"
                sx={{ width: 155, mb: 2 }}
              />
            </Box>

            

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="text"
                // type="email"
                value={formData.email}
                onChange={handleInputChange}
                margin="normal"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme === 'dark' ? '#87cefa' : '#2791d2',
                    },
                    '&.Mui-focused .MuiInputLabel-root': {
                      color: theme === 'dark' ? '#87cefa' : '#2791d2',
                    },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: theme === 'dark' 
                        ? '0 0 0 100px rgba(30, 30, 30, 0.95) inset'
                        : '0 0 0 100px rgba(255, 255, 255, 0.95) inset',
                      WebkitTextFillColor: theme === 'dark' ? '#fff' : '#000',
                      transition: 'background-color 5000s ease-in-out 0s',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment 
                      position="start"
                      sx={{
                        backgroundColor: 'transparent',
                        '& .MuiInputAdornment-root': {
                          backgroundColor: 'transparent',
                        }
                      }}
                    >
                      <Mail sx={{ color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange}
                margin="normal"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme === 'dark' ? '#87cefa' : '#2791d2',
                    },
                    '&.Mui-focused .MuiInputLabel-root': {
                      color: theme === 'dark' ? '#87cefa' : '#2791d2',
                    },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: theme === 'dark' 
                        ? '0 0 0 100px rgba(30, 30, 30, 0.95) inset'
                        : '0 0 0 100px rgba(255, 255, 255, 0.95) inset',
                      WebkitTextFillColor: theme === 'dark' ? '#fff' : '#000',
                      transition: 'background-color 5000s ease-in-out 0s',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment 
                      position="start"
                      sx={{
                        backgroundColor: 'transparent',
                        '& .MuiInputAdornment-root': {
                          backgroundColor: 'transparent',
                        }
                      }}
                    >
                      <Lock sx={{ color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment 
                      position="end"
                      sx={{
                        backgroundColor: 'transparent',
                        '& .MuiInputAdornment-root': {
                          backgroundColor: 'transparent',
                        }
                      }}
                    >
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? 
                          <VisibilityOff sx={{ color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }} /> : 
                          <Visibility sx={{ color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }} />
                        }
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  textTransform: 'none',
                  background: theme === 'dark' ? '#87cefa' : '#2791d2',
                  '&:hover': {
                    background: theme === 'dark' ? '#5f9eb3' : '#1a6b9e',
                  },
                }}
              >
                Sign In
              </Button>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                <Link
                  to="/forgot-password"
                  style={{
                    color: theme === 'dark' ? '#87cefa' : '#2791d2',
                    textDecoration: 'none',
                    fontWeight: 500,
                  }}
                >
                  Forgot Password?
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Login; 
